import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileManager } from '@cubone/react-file-manager';
import '@cubone/react-file-manager/dist/style.css';
import { ArrowLeft, History, RotateCcw, Trash2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { RuntimeConfig } from '@/lib/runtime-config';
import { formatDateTime, formatRelativeTime } from '@/lib/format';
import {
  getDownloadUrl,
  getRecycleBin,
  getRepoEntries,
  listRepos,
  listSnapshots,
  restoreFile,
  restoreSnapshot,
  type RecycleBinItem,
  type RecycleBinResponse,
  type RepoEntriesResponse,
  type RepoSnapshot,
  type RepoSummary,
  WebApiError
} from '@/lib/web-api';

interface RepoRecyclePageProps {
  runtimeConfig: RuntimeConfig;
  tenantId: string;
}

type FileManagerEntry = {
  name: string;
  isDirectory: boolean;
  path: string;
  updatedAt?: string;
  size?: number;
};

const toIso = (unixMs?: number) => (unixMs ? new Date(unixMs).toISOString() : undefined);

const normalizePrefix = (value: string) => value.replace(/^\/+/, '').replace(/\/+$/, '');

const splitRepoPath = (path: string, repoId: string) => {
  const cleaned = path.replace(/^\/+/, '');
  const segments = cleaned.split('/').filter(Boolean);
  if (segments.length === 0 || segments[0] !== repoId) {
    return null;
  }
  return segments.slice(1).join('/');
};

const splitSnapshotPath = (path: string) => {
  const cleaned = path.replace(/^\/+/, '');
  const segments = cleaned.split('/').filter(Boolean);
  if (segments.length === 0) {
    return { snapshotId: null, repoPath: '' };
  }
  return {
    snapshotId: segments[0] ?? null,
    repoPath: segments.slice(1).join('/')
  };
};

export const RepoRecyclePage = ({ runtimeConfig, tenantId }: RepoRecyclePageProps) => {
  const navigate = useNavigate();
  const params = useParams();
  const repoId = params.repoId;
  const resolvedRepoId = repoId ?? '';
  const [bin, setBin] = useState<RecycleBinResponse | null>(null);
  const [snapshots, setSnapshots] = useState<RepoSnapshot[]>([]);
  const [repos, setRepos] = useState<RepoSummary[]>([]);
  const [snapshotEntries, setSnapshotEntries] = useState<RepoEntriesResponse | null>(null);
  const [snapshotPath, setSnapshotPath] = useState('');
  const [selectedDeleted, setSelectedDeleted] = useState<FileManagerEntry[]>([]);
  const [selectedSnapshots, setSelectedSnapshots] = useState<FileManagerEntry[]>([]);
  const [restorePrefix, setRestorePrefix] = useState('');
  const [restoreRepoId, setRestoreRepoId] = useState(repoId ?? '');
  const [restoreMode, setRestoreMode] = useState<'current' | 'new'>('current');
  const [isLoading, setIsLoading] = useState(true);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (repoId) {
      setRestoreRepoId(repoId);
    }
  }, [repoId]);

  const refreshData = useCallback(async () => {
    if (!repoId) {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const [binResponse, snapshotResponse] = await Promise.all([
        getRecycleBin(runtimeConfig, tenantId, repoId),
        listSnapshots(runtimeConfig, tenantId, repoId)
      ]);
      const repoList = await listRepos(runtimeConfig, tenantId).catch(() => []);
      setBin(binResponse);
      setSnapshots(snapshotResponse);
      setRepos(repoList);
    } catch (err) {
      if (err instanceof WebApiError) {
        setError(err.code === 'IndexNotReady'
          ? 'Snapshot index is still building. Try again in a moment.'
          : err.message);
      } else {
        setError('Unable to load recycle bin.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [runtimeConfig, tenantId, repoId]);

  useEffect(() => {
    if (repoId) {
      setMessage(null);
      void refreshData();
    }
  }, [refreshData, repoId]);

  const snapshotSegments = useMemo(
    () => snapshotPath.replace(/^\/+/, '').split('/').filter(Boolean),
    [snapshotPath]
  );
  const activeSnapshotId = snapshotSegments[0] ?? null;
  const activeSnapshotPath = snapshotSegments.slice(1).join('/');

  const refreshSnapshotEntries = useCallback(async () => {
    if (!repoId || !activeSnapshotId) {
      setSnapshotEntries(null);
      setSnapshotError(null);
      return;
    }
    setSnapshotLoading(true);
    setSnapshotError(null);
    try {
      const entries = await getRepoEntries(
        runtimeConfig,
        tenantId,
        repoId,
        activeSnapshotPath || undefined,
        undefined,
        activeSnapshotId
      );
      setSnapshotEntries(entries);
    } catch (err) {
      if (err instanceof WebApiError) {
        setSnapshotError(
          err.code === 'IndexNotReady'
            ? 'Snapshot index is still building. Try again in a moment.'
            : err.message
        );
      } else {
        setSnapshotError('Unable to load snapshot entries.');
      }
    } finally {
      setSnapshotLoading(false);
    }
  }, [runtimeConfig, tenantId, repoId, activeSnapshotId, activeSnapshotPath]);

  useEffect(() => {
    void refreshSnapshotEntries();
  }, [refreshSnapshotEntries]);

  const deletedFiles = useMemo(() => bin?.deleted_files ?? [], [bin]);
  const deletedByPath = useMemo(
    () => new Map(deletedFiles.map(item => [item.path, item])),
    [deletedFiles]
  );
  const latestSnapshot = useMemo(
    () => snapshots.find(snapshot => snapshot.snapshot_id === bin?.latest_snapshot_id) ?? null,
    [snapshots, bin]
  );

  const selectedSnapshotId = useMemo(() => {
    const first = selectedSnapshots[0];
    if (!first) {
      return null;
    }
    return splitSnapshotPath(first.path).snapshotId;
  }, [selectedSnapshots]);

  const snapshotRestoreId = activeSnapshotId ?? selectedSnapshotId;
  const snapshotInfo = useMemo(
    () => snapshots.find(snapshot => snapshot.snapshot_id === snapshotRestoreId) ?? null,
    [snapshots, snapshotRestoreId]
  );
  const restoreTargetRepo = restoreMode === 'current'
    ? resolvedRepoId
    : restoreRepoId.trim();

  const deletedFileManagerFiles = useMemo(() => {
    if (!repoId) {
      return [];
    }
    const map = new Map<string, FileManagerEntry>();
    const add = (entry: FileManagerEntry) => {
      if (!map.has(entry.path)) {
        map.set(entry.path, entry);
      }
    };

    const rootPath = `/${repoId}`;
    add({
      name: repoId,
      isDirectory: true,
      path: rootPath
    });

    deletedFiles.forEach(item => {
      const segments = item.path.split('/').filter(Boolean);
      if (segments.length === 0) {
        return;
      }
      let pathAccumulator = rootPath;
      segments.slice(0, -1).forEach(segment => {
        pathAccumulator = `${pathAccumulator}/${segment}`;
        add({
          name: segment,
          isDirectory: true,
          path: pathAccumulator
        });
      });

      const filePath = `${rootPath}/${segments.join('/')}`.replace(/\/+/g, '/');
      add({
        name: segments[segments.length - 1],
        isDirectory: false,
        path: filePath,
        updatedAt: toIso(item.mtime_unix_ms ?? item.last_snapshot_created_at_unix_ms),
        size: item.size_bytes
      });
    });

    return Array.from(map.values());
  }, [deletedFiles, repoId]);

  const snapshotFileManagerFiles = useMemo(() => {
    const map = new Map<string, FileManagerEntry>();
    const add = (entry: FileManagerEntry) => {
      if (!map.has(entry.path)) {
        map.set(entry.path, entry);
      }
    };

    snapshots.forEach(snapshot => {
      add({
        name: snapshot.snapshot_id,
        isDirectory: true,
        path: `/${snapshot.snapshot_id}`,
        updatedAt: toIso(snapshot.created_at_unix_ms)
      });
    });

    if (activeSnapshotId) {
      let pathAccumulator = `/${activeSnapshotId}`;
      add({
        name: activeSnapshotId,
        isDirectory: true,
        path: pathAccumulator,
        updatedAt: toIso(snapshotInfo?.created_at_unix_ms)
      });

      activeSnapshotPath.split('/').filter(Boolean).forEach(segment => {
        pathAccumulator = `${pathAccumulator}/${segment}`;
        add({
          name: segment,
          isDirectory: true,
          path: pathAccumulator
        });
      });

      (snapshotEntries?.entries ?? []).forEach(entry => {
        const entryName = entry.path.split('/').filter(Boolean).pop() ?? entry.path;
        if (!entryName) {
          return;
        }
        const entryPath = `/${activeSnapshotId}/${entry.path}`.replace(/\/+/g, '/');
        add({
          name: entryName,
          isDirectory: entry.type === 'dir',
          path: entryPath,
          updatedAt: toIso(entry.mtime_unix_ms),
          size: entry.size
        });
      });
    }

    return Array.from(map.values());
  }, [snapshots, activeSnapshotId, snapshotInfo, activeSnapshotPath, snapshotEntries]);

  const collectDeletedTargets = useCallback((selection: FileManagerEntry[]) => {
    if (!repoId) {
      return [];
    }
    const targets = new Map<string, RecycleBinItem>();
    selection.forEach(item => {
      const repoPath = splitRepoPath(item.path, repoId);
      if (repoPath === null) {
        return;
      }
      if (item.isDirectory) {
        const prefix = repoPath ? `${repoPath}/` : '';
        deletedFiles.forEach(file => {
          if (!repoPath || file.path === repoPath || file.path.startsWith(prefix)) {
            targets.set(file.path, file);
          }
        });
        return;
      }
      const file = deletedByPath.get(repoPath);
      if (file) {
        targets.set(file.path, file);
      }
    });
    return Array.from(targets.values());
  }, [repoId, deletedFiles, deletedByPath]);

  const handleDeletedDownload = useCallback(async (files: FileManagerEntry[]) => {
    if (!repoId) {
      return;
    }
    for (const file of files) {
      if (file.isDirectory) {
        continue;
      }
      const repoPath = splitRepoPath(file.path, repoId);
      if (!repoPath) {
        continue;
      }
      const item = deletedByPath.get(repoPath);
      if (!item) {
        continue;
      }
      try {
        const url = await getDownloadUrl(runtimeConfig, tenantId, repoId, repoPath, item.last_snapshot_id);
        window.location.assign(url);
      } catch (err) {
        if (err instanceof WebApiError) {
          setError(err.message);
        } else {
          setError('Unable to download file.');
        }
      }
    }
  }, [runtimeConfig, tenantId, repoId, deletedByPath]);

  const handleSnapshotDownload = useCallback(async (files: FileManagerEntry[]) => {
    if (!repoId) {
      return;
    }
    for (const file of files) {
      if (file.isDirectory) {
        continue;
      }
      const { snapshotId, repoPath } = splitSnapshotPath(file.path);
      if (!snapshotId || !repoPath) {
        continue;
      }
      try {
        const url = await getDownloadUrl(runtimeConfig, tenantId, repoId, repoPath, snapshotId);
        window.location.assign(url);
      } catch (err) {
        if (err instanceof WebApiError) {
          setError(err.message);
        } else {
          setError('Unable to download file.');
        }
      }
    }
  }, [runtimeConfig, tenantId, repoId]);

  const handleRestoreDeleted = useCallback(async (restoreToFolder: boolean) => {
    if (!repoId) {
      return;
    }
    const targets = collectDeletedTargets(selectedDeleted);
    if (targets.length === 0) {
      setError('Select a file or folder to restore.');
      return;
    }
    let destinationPrefix = '';
    if (restoreToFolder) {
      const response = window.prompt('Restore into folder (leave empty for original location).', '');
      if (response === null) {
        return;
      }
      destinationPrefix = normalizePrefix(response);
    }
    setActionLoading(true);
    setError(null);
    try {
      for (const target of targets) {
        const destinationPath = destinationPrefix ? `${destinationPrefix}/${target.path}` : undefined;
        await restoreFile(
          runtimeConfig,
          tenantId,
          repoId,
          target.path,
          target.last_snapshot_id,
          destinationPath
        );
      }
      await refreshData();
      setSelectedDeleted([]);
      setMessage(`Restored ${targets.length} file${targets.length === 1 ? '' : 's'}.`);
    } catch (err) {
      if (err instanceof WebApiError) {
        setError(err.message);
      } else {
        setError('Restore failed.');
      }
    } finally {
      setActionLoading(false);
    }
  }, [
    collectDeletedTargets,
    refreshData,
    repoId,
    runtimeConfig,
    tenantId,
    selectedDeleted
  ]);

  const handleRestoreSnapshot = useCallback(async () => {
    if (!repoId) {
      return;
    }
    if (!snapshotRestoreId) {
      setError('Select a snapshot to restore.');
      return;
    }
    if (restoreMode === 'new' && !restoreTargetRepo) {
      setError('Enter a new repository ID.');
      return;
    }
    if (!window.confirm('Restore this snapshot? The current snapshot will remain available.')) {
      return;
    }
    const destinationPrefix = normalizePrefix(restorePrefix);
    setActionLoading(true);
    setError(null);
    try {
      const response = await restoreSnapshot(
        runtimeConfig,
        tenantId,
        repoId,
        snapshotRestoreId,
        destinationPrefix || undefined,
        restoreTargetRepo !== resolvedRepoId ? restoreTargetRepo : undefined
      );
      await refreshData();
      setMessage(`Snapshot restored as ${response.snapshot_id}.`);
    } catch (err) {
      if (err instanceof WebApiError) {
        setError(err.message);
      } else {
        setError('Snapshot restore failed.');
      }
    } finally {
      setActionLoading(false);
    }
  }, [
    snapshotRestoreId,
    restorePrefix,
    restoreTargetRepo,
    resolvedRepoId,
    restoreMode,
    repoId,
    runtimeConfig,
    tenantId,
    refreshData
  ]);

  if (!repoId) {
    return (
      <Alert className="border-error/30 bg-error/10">
        <AlertTitle>Repository not found</AlertTitle>
        <AlertDescription>Return to the repositories list and choose a valid repo.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/repos/${repoId}`)}
        >
          <ArrowLeft size={16} />
          <span className="ml-2">Back to repository</span>
        </Button>
        <div>
          <h1 className="text-xl font-semibold">Recycle Bin</h1>
          {latestSnapshot?.created_at_unix_ms ? (
            <p className="text-sm text-muted-foreground">
              Latest snapshot {formatRelativeTime(latestSnapshot.created_at_unix_ms)}.
            </p>
          ) : null}
        </div>
      </div>

      {error ? (
        <Alert className="border-error/30 bg-error/10">
          <AlertTitle>Unable to load</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {message ? (
        <Alert className="border-primary/30 bg-primary/10">
          <AlertTitle>Action completed</AlertTitle>
          <AlertDescription>{message}</AlertDescription>
        </Alert>
      ) : null}

      <Tabs defaultValue="deleted" className="space-y-4">
        <TabsList>
          <TabsTrigger value="deleted">Deleted files</TabsTrigger>
          <TabsTrigger value="snapshots">Repository snapshots</TabsTrigger>
        </TabsList>

        <TabsContent value="deleted" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="flex items-center gap-2">
                <Trash2 size={18} />
                Deleted Files
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">{deletedFiles.length} items</Badge>
                <Button
                  size="sm"
                  disabled={actionLoading || selectedDeleted.length === 0}
                  onClick={() => void handleRestoreDeleted(false)}
                >
                  <RotateCcw className="h-4 w-4" />
                  Restore
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={actionLoading || selectedDeleted.length === 0}
                  onClick={() => void handleRestoreDeleted(true)}
                >
                  <RotateCcw className="h-4 w-4" />
                  Restore to folder
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-24 w-full" />
              ) : deletedFiles.length === 0 ? (
                <Alert className="border-border/60 bg-muted/40">
                  <AlertTitle>Nothing in the recycle bin yet</AlertTitle>
                  <AlertDescription>Deleted files will appear here once a snapshot is published.</AlertDescription>
                </Alert>
              ) : (
                <FileManager
                  files={deletedFileManagerFiles}
                  isLoading={isLoading || actionLoading}
                  initialPath={`/${repoId}`}
                  layout="list"
                  height="560px"
                  primaryColor="#242436"
                  collapsibleNav
                  permissions={{
                    create: false,
                    upload: false,
                    move: false,
                    rename: false,
                    delete: false,
                    copy: false,
                    download: true
                  }}
                  onDownload={handleDeletedDownload}
                  onFileOpen={(file: FileManagerEntry) => {
                    if (!file.isDirectory) {
                      void handleDeletedDownload([file]);
                    }
                  }}
                  onSelectionChange={(files: FileManagerEntry[]) => setSelectedDeleted(files)}
                  onRefresh={() => void refreshData()}
                  onError={(error: { message?: string } | null) => {
                    setError(error?.message || 'File manager error.');
                  }}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="snapshots" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="flex items-center gap-2">
                <History size={18} />
                Repository Snapshots
              </CardTitle>
              <Badge variant="outline">{snapshots.length} snapshots</Badge>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[180px] space-y-1">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Active snapshot</p>
                  <p className="text-sm font-medium">{snapshotRestoreId ?? 'Select a snapshot'}</p>
                  <p className="text-xs text-muted-foreground">
                    {snapshotInfo?.created_at_unix_ms
                      ? formatDateTime(snapshotInfo.created_at_unix_ms)
                      : '—'}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-muted-foreground">Restore folder (optional)</label>
                  <input
                    className="h-9 w-56 rounded-md border border-input bg-background px-3 text-sm"
                    placeholder="e.g. restored/2025-01-12"
                    value={restorePrefix}
                    onChange={event => setRestorePrefix(event.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs text-muted-foreground">Restore target</label>
                  <div className="flex items-center gap-3 text-sm">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="restore-mode"
                        checked={restoreMode === 'current'}
                        onChange={() => setRestoreMode('current')}
                      />
                      Current repo
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="restore-mode"
                        checked={restoreMode === 'new'}
                        onChange={() => setRestoreMode('new')}
                      />
                      New repo
                    </label>
                  </div>
                  {restoreMode === 'new' ? (
                    <input
                      className="h-9 w-48 rounded-md border border-input bg-background px-3 text-sm"
                      list="repo-options"
                      placeholder="new-repo-id"
                      value={restoreRepoId}
                      onChange={event => setRestoreRepoId(event.target.value)}
                    />
                  ) : (
                    <div className="text-xs text-muted-foreground">{resolvedRepoId}</div>
                  )}
                  <datalist id="repo-options">
                    {repos.map(repo => (
                      <option key={repo.repo_id} value={repo.repo_id}>
                        {repo.repo_name ?? repo.repo_id}
                      </option>
                    ))}
                  </datalist>
                </div>
                <Button
                  size="sm"
                  disabled={actionLoading || !snapshotRestoreId || snapshotInfo?.index_ready === false}
                  onClick={() => void handleRestoreSnapshot()}
                >
                  <RotateCcw className="h-4 w-4" />
                  Restore snapshot
                </Button>
              </div>

              {snapshotError ? (
                <Alert className="border-error/30 bg-error/10">
                  <AlertTitle>Unable to load snapshot</AlertTitle>
                  <AlertDescription>{snapshotError}</AlertDescription>
                </Alert>
              ) : null}

              {isLoading ? (
                <Skeleton className="h-24 w-full" />
              ) : snapshots.length === 0 ? (
                <Alert className="border-border/60 bg-muted/40">
                  <AlertTitle>No snapshots available yet</AlertTitle>
                  <AlertDescription>Snapshots will appear as soon as a sync completes.</AlertDescription>
                </Alert>
              ) : (
                <FileManager
                  files={snapshotFileManagerFiles}
                  isLoading={snapshotLoading || actionLoading}
                  initialPath={snapshotPath}
                  layout="list"
                  height="560px"
                  primaryColor="#242436"
                  collapsibleNav
                  permissions={{
                    create: false,
                    upload: false,
                    move: false,
                    rename: false,
                    delete: false,
                    copy: false,
                    download: true
                  }}
                  onFolderChange={(path: string) => setSnapshotPath(path)}
                  onDownload={handleSnapshotDownload}
                  onFileOpen={(file: FileManagerEntry) => {
                    if (!file.isDirectory) {
                      void handleSnapshotDownload([file]);
                    }
                  }}
                  onSelectionChange={(files: FileManagerEntry[]) => setSelectedSnapshots(files)}
                  onRefresh={() => {
                    void refreshData();
                    void refreshSnapshotEntries();
                  }}
                  onError={(error: { message?: string } | null) => {
                    setSnapshotError(error?.message || 'File manager error.');
                  }}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
